import { useCallback } from "react";
import { Spacings, View } from "react-native-ui-lib";
import PaymentMethod from "../../../../../../utilities/data/model/PaymentMethod";
import { usePaymentControllerProvider } from "../../../../utils/payment-controller";
import React from "react";
import { AddPaymentOptions } from "../../../../utils/payment-controller/types";
import Sale from "../../../../../../utilities/data/model/Sale";
import { PaymentScreenProps } from "../../PaymentScreen";
import { FlatList } from "react-native";
import { moneyToFormattedString } from "@towersystems/roam-common/lib/shared-utils";
import {
  Button,
  Page,
  TextField,
  Stack,
  Text,
} from "@towersystems/roam-common-native";
import { usePaymentActionsContextProvider } from "../../../PaymentRootScreen/hooks";
import { useCheckoutContextProvider } from "../../../../utils/checkout";

export interface DefaultPaymentProps {
  paymentMethod: PaymentMethod;
  sale: Sale;
  navigation: PaymentScreenProps["navigation"];
  minTenderAmount?: number;
}

export const DefaultPayment = ({
  paymentMethod,
  sale,
  navigation,
  minTenderAmount: _minTenderAmount,
  ...rest
}: DefaultPaymentProps) => {
  const [error, setError] = React.useState<string | undefined>(undefined);

  const maybeParentScreenContext = usePaymentActionsContextProvider();

  const [paymentAmount, setPaymentAmount] = React.useState<string | undefined>(
    _minTenderAmount?.toString()
  );
  const { balance } = useCheckoutContextProvider();
  const {
    tenderedAmountInput,
    validTenderedAmount,
    addPayment,
    setTenderedAmountInput,
  } = usePaymentControllerProvider();

  const overTendered =
    validTenderedAmount && Number(tenderedAmountInput) > balance;

  const normalizedValidTenderedAmount = React.useMemo(() => {
    if (!validTenderedAmount) {
      return false;
    }

    if (overTendered) {
      return false;
    }

    if (_minTenderAmount) {
      return Number(tenderedAmountInput) >= _minTenderAmount;
    }

    return true;
  }, [
    validTenderedAmount,
    overTendered,
    tenderedAmountInput,
    _minTenderAmount,
  ]);

  const handleOnAddPayment = useCallback(
    async (options?: AddPaymentOptions) => {
      try {
        if (!paymentAmount) {
          throw new Error("Invalid payment amount");
        }

        const tenderedAmount =
          options?.tenderedAmount || options?.tenderedAmount === 0
            ? options.tenderedAmount
            : parseFloat(tenderedAmountInput);

        await maybeParentScreenContext?.onAddPayment?.({
          ...options,
          paymentAmount: paymentAmount,
          paymentMethod: paymentMethod,
          tenderedAmount: tenderedAmount.toString(),
        });
        return;
      } catch (error: any) {
        setError(error.message);
      }
    },
    [
      paymentMethod,
      paymentAmount,
      tenderedAmountInput,
      validTenderedAmount,
      sale,
    ]
  );

  React.useEffect(() => {
    navigation.setOptions({
      title: `Pay ${moneyToFormattedString(paymentAmount)}`,
      headerRight: () => (
        <Button
          label="Collect Payment"
          isAction
          disabled={!normalizedValidTenderedAmount}
          onPress={() => handleOnAddPayment()}
          size="compact"
        ></Button>
      ),
    });
  }, [normalizedValidTenderedAmount, paymentAmount, handleOnAddPayment]);

  return (
    <Page
      titleContainerStyle={{ alignItems: "center" }}
      title={paymentMethod.name}
    >
      <View>
        <View style={{ alignItems: "center" }} marginT-20>
          <View flex row style={{ width: "100%" }}>
            <View flex>
              <TextField
                keyboardType="numeric"
                placeholder="Other amount"
                onChangeText={setTenderedAmountInput}
                value={tenderedAmountInput}
                formatterType={
                  normalizedValidTenderedAmount ? "money" : undefined
                }
                validationMessage={
                  overTendered
                    ? "Tender limit exceeded"
                    : tenderedAmountInput &&
                      (_minTenderAmount && !normalizedValidTenderedAmount
                        ? `Minimum tender amount is ${moneyToFormattedString(
                            _minTenderAmount
                          )}`
                        : !validTenderedAmount
                        ? "Invalid amount"
                        : undefined)
                }
              />
            </View>
          </View>

          {error ? <Text>{error}</Text> : null}
        </View>
      </View>
    </Page>
  );
};
